import type { SnapshotFrom } from "xstate";

import type { StepComponentKey, StepDisplayInfo, StepMeta } from "@/types/scheduler-machine";

import { schedulerMachine } from "./schedulerMachine";
import { getStepOrder, type WizardStep } from "./utils/scheduler-flow.util";

type SchedulerSnapshot = SnapshotFrom<typeof schedulerMachine>;
type StepState = WizardStep | "reasonForVisit";

/* --------------------------------
   Step display info
   --------------------------------
   Every top-level machine state is a visible step and carries its display info as `meta`.
   The order the steps appear in comes from getStepOrder.
   -------------------------------- */

const STEP_META = Object.fromEntries(
  Object.entries(schedulerMachine.states).map(([state, node]) => [state, node.meta]),
) as Record<StepState, StepMeta>;

const STEP_STATES = Object.keys(STEP_META) as StepState[];

export const NO_SIDEBAR_STEP_INDEX = 0;

function getActiveStep(snapshot: SchedulerSnapshot): StepState | undefined {
  return STEP_STATES.find((state) => snapshot.matches(state));
}

export function getSidebarSteps(snapshot: SchedulerSnapshot) {
  return getStepOrder(snapshot.context).map((machineState, i) => ({
    machineState,
    ...STEP_META[machineState],
    stepIndex: i + 1,
    // Derived from position so reordered flows render correctly. Review and date/time never show
    // back (review by design, date/time because it renders without a footer to hold it).
    showBackButton:
      i > 0 && machineState !== "appointmentReview" && machineState !== "dateTimeSelection",
  }));
}

const DEFAULT_STEP_INFO: StepDisplayInfo = {
  stepIndex: 0,
  showBackButton: false,
  showFooter: false,
  label: "",
};

export function getStepInfoFromState(
  snapshot: SchedulerSnapshot,
  disableGridLayout = false,
): StepDisplayInfo {
  const activeStep = getActiveStep(snapshot);
  if (!activeStep) return DEFAULT_STEP_INFO;

  const sidebarMatch = getSidebarSteps(snapshot).find(
    ({ machineState }) => machineState === activeStep,
  );
  if (sidebarMatch) return sidebarMatch;

  // Reason for visit is the only step outside the sidebar. In the old list layout it needs no
  // footer, because items advance on click.
  return {
    ...STEP_META[activeStep],
    stepIndex: NO_SIDEBAR_STEP_INDEX,
    showBackButton: false,
    showFooter:
      activeStep === "reasonForVisit" ? !disableGridLayout : STEP_META[activeStep].showFooter,
  };
}

export function getStepComponentKey(snapshot: SchedulerSnapshot): StepComponentKey {
  const activeStep = getActiveStep(snapshot);
  return activeStep ? STEP_META[activeStep].componentKey : "reason-for-visit";
}

/* --------------------------------
   Loading state detection
   -------------------------------- */

export function isSubmittingState(snapshot: SchedulerSnapshot): boolean {
  return snapshot.hasTag("submitting");
}

export function isValidatingPatientState(snapshot: SchedulerSnapshot): boolean {
  return snapshot.hasTag("validatingPatient");
}

export function isPatientRefusedState(snapshot: SchedulerSnapshot): boolean {
  return snapshot.hasTag("patientRefused");
}
